import { useEditCampaignSipTrunk } from '@features/Admin/Campaign/hooks/useEditCampaignSipTrunk';
import { Box, Grid, Typography, useTheme } from '@mui/material';
import { CustomSuccessToast } from '@shared/components/customSuccessToast';
import { SipSettingsForm } from '@shared/components/forms/SipSettingsForm';
import {
  CampaignDto,
  CampaignSipTrunkCreateRequestDto,
  useCampaignSipTrunkControllerGetSipTrunkQuery,
} from '@shared/services/apiService';
import { SipTruncFieldsError } from '@shared/types/admin/campaign';
import { t } from 'i18next';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { mapCampaignSipToDefaultSip } from '../utils';
import { IconPhoneCheck } from '@tabler/icons-react';
import { RootState, useAppSelector } from '@shared/store';
import { UserRolesEnum } from '@shared/constants/auth.constants';
import { CampaignSectionSaveActions } from '../EditCampaignSectionActions';

interface SipTrunkTabProps {
  campaign: CampaignDto;
}

const SipTrunkTab: React.FC<SipTrunkTabProps> = ({ campaign }: SipTrunkTabProps) => {
  const theme = useTheme();
  const currentUserRole = useAppSelector((state: RootState) => state.auth.authenticatedUser?.role);
  const isReadOnly = currentUserRole === UserRolesEnum.VIEWER;

  const [isSipValid, setSipValid] = useState(true);
  const [actualSipTrunkSetting, setActualSipTrunkSetting] =
    useState<CampaignSipTrunkCreateRequestDto | null>(null);
  const [, setSipTrunkCreateFieldError] = useState<SipTruncFieldsError>({
    type: [],
    authUsername: [],
    authPassword: [],
    numbers: [],
    address: [],
    transport: [],
    editErrorMessage: '',
  });

  const { data: campaignSipTrunk, refetch: refetchCampaignSip } =
    useCampaignSipTrunkControllerGetSipTrunkQuery({
      id: campaign.id,
    });

  const savedSipTrunkSetting = useMemo(
    () => (campaignSipTrunk ? mapCampaignSipToDefaultSip(campaignSipTrunk) : null),
    [campaignSipTrunk],
  );

  const notify = () => {
    toast.success(CustomSuccessToast, {
      closeButton: false,
      autoClose: 5000,
      icon: false,
    });
  };

  const { onSubmit, formMethods } = useEditCampaignSipTrunk({
    setSipTrunkCreateFieldError,
    onSuccess: notify,
  });
  const { reset, setValue } = formMethods;

  const handleSipSettingsChange = (settings: CampaignSipTrunkCreateRequestDto | null) => {
    setActualSipTrunkSetting(settings);

    setValue('type', settings?.type ?? campaign.type);
    setValue('numbers', settings?.numbers ?? []);
    setValue('address', settings?.address ?? undefined);
    setValue('transport', settings?.transport);
    setValue('allowedAddresses', settings?.allowedAddresses ?? undefined);
    setValue('authUsername', settings?.authUsername ?? undefined);
    setValue('authPassword', settings?.authPassword ?? undefined);
  };

  const submitSipTrunk = async () => {
    try {
      await onSubmit();
      void refetchCampaignSip();
    } catch (error) {
      console.error('Failed submitting sip trunk:', error);
    }
  };

  const handleSipTrunkCancel = useCallback(() => {
    setActualSipTrunkSetting(savedSipTrunkSetting);
    reset({
      campaignId: campaign.id,
      address: savedSipTrunkSetting?.address || undefined,
      authPassword: savedSipTrunkSetting?.authPassword || undefined,
      authUsername: savedSipTrunkSetting?.authUsername || undefined,
      numbers: savedSipTrunkSetting?.numbers ?? [],
      transport: savedSipTrunkSetting?.transport || undefined,
      allowedAddresses: savedSipTrunkSetting?.allowedAddresses || undefined,
      type: campaign.type,
    });
  }, [campaign.id, campaign.type, reset, savedSipTrunkSetting]);

  useEffect(() => {
    setValue('campaignId', campaign.id);
  }, [campaign.id, setValue]);

  useEffect(() => {
    setActualSipTrunkSetting(savedSipTrunkSetting);
  }, [savedSipTrunkSetting]);

  useEffect(() => {
    reset({
      campaignId: campaign.id,
      address: actualSipTrunkSetting?.address || undefined,
      authPassword: actualSipTrunkSetting?.authPassword || undefined,
      authUsername: actualSipTrunkSetting?.authUsername || undefined,
      numbers: actualSipTrunkSetting?.numbers ?? [],
      transport: actualSipTrunkSetting?.transport || undefined,
      allowedAddresses: actualSipTrunkSetting?.allowedAddresses || undefined,
      type: campaign.type,
    });
  }, [actualSipTrunkSetting, campaign.id, campaign.type, reset]);

  return (
    <Grid
      container
      sx={{
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 2, sm: 3 },
        boxSizing: 'border-box',
        flexDirection: 'column',
        width: '100%',
      }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          marginBottom: '20px',
          width: '100%',
          minHeight: 36,
        }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            minHeight: 36,
            'svg path': {
              stroke: theme.palette.getContrastText(theme.palette.background.default),
            },
          }}>
          <IconPhoneCheck size={22} stroke={1.5} />
          <Typography
            component="span"
            sx={{
              fontSize: '18px',
              fontWeight: 600,
              lineHeight: '36px',
              display: 'inline-block',
            }}>
            {t('campaignPage.editCampaign.sipTrunk')}
          </Typography>
        </Box>
        <CampaignSectionSaveActions
          isReadOnly={isReadOnly}
          isDirty={true}
          isSaving={false}
          isSaveDisabled={isReadOnly || !isSipValid}
          onSave={() => void submitSipTrunk()}
          onCancel={handleSipTrunkCancel}
        />
      </Box>
      <Box sx={{ maxWidth: 720, width: '100%' }}>
        <SipSettingsForm
          settings={actualSipTrunkSetting}
          onChange={handleSipSettingsChange}
          onValidationChange={setSipValid}
          type={campaign.type}
          disabled={isReadOnly}
          hideTitle
        />
      </Box>
    </Grid>
  );
};

export default SipTrunkTab;
